import { SnapshotCard } from '@ovComponents/3-pattern/snapshotCard';
import { Box, Skeleton, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { ActivitiesMetricsTiles } from '../utils/mapActivitiesMetrics';

const TILE_TITLE_SX = {
  fontWeight: 700,
  textTransform: 'uppercase',
} as const;

export type ActivitiesMetricsDashboardProps = {
  tiles: ActivitiesMetricsTiles;
  loading: boolean;
  /** True when a metrics payload is available to display. */
  hasData?: boolean;
  error?: Error;
};

const TILES = [
  {
    key: 'occurringToday',
    titleKey: 'activitiesV3.metrics.occurringToday.title',
    hintKey: 'activitiesV3.metrics.occurringToday.hint',
    tone: 'neutral' as const,
  },
  {
    key: 'created',
    titleKey: 'activitiesV3.metrics.created.title',
    hintKey: 'activitiesV3.metrics.created.hint',
    tone: 'neutral' as const,
  },
  {
    key: 'reconciledToday',
    titleKey: 'activitiesV3.metrics.reconciledToday.title',
    hintKey: 'activitiesV3.metrics.reconciledToday.hint',
    tone: 'positive' as const,
  },
  {
    key: 'agingPending',
    titleKey: 'activitiesV3.metrics.agingPending.title',
    hintKey: 'activitiesV3.metrics.agingPending.hint',
    tone: 'negative' as const,
  },
] as const;

export const ActivitiesMetricsDashboard = ({
  tiles,
  loading,
  hasData = false,
  error,
}: ActivitiesMetricsDashboardProps) => {
  const { t } = useTranslation(['reconciliation']);
  const showUnavailableValues = Boolean(error) && !hasData && !loading;

  const tileTitle = (titleKey: string) => (
    <Typography
      component='span'
      variant='labelSmall'
      color='textSecondary'
      sx={TILE_TITLE_SX}
    >
      {t(titleKey)}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {error && (
        <Typography variant='bodyMedium' color='error'>
          {t('activitiesV3.metrics.error')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {TILES.map((tile) => (
          <SnapshotCard
            key={tile.key}
            title={tileTitle(tile.titleKey)}
            primaryValue={
              loading ? (
                <Skeleton variant='text' width={48} height={36} />
              ) : showUnavailableValues ? (
                '—'
              ) : (
                tiles[tile.key].toLocaleString()
              )
            }
            tone={tile.tone}
            surface='accentBorder'
            footer={t(tile.hintKey)}
            compact
          />
        ))}
      </Box>
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px dashed',
          borderColor: 'divider',
          minHeight: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 3,
        }}
        data-testid='activities-backlog-aging-chart-slot'
      >
        <Typography variant='bodyMedium' color='textSecondary'>
          {t('activitiesV3.chartSlot.placeholder')}
        </Typography>
      </Box>
    </Box>
  );
};
